import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

// Drop-in replacement for React.lazy that auto-recovers from stale-chunk
// failures after a deploy.
//
// Each build stamps a content hash into every route chunk filename. When we
// ship a new deploy, the production domain serves only the new hashes — the old
// ones 404. A browser tab still running the OLD index.html (loaded before the
// deploy) will request an old hash on its next lazy navigation and hit
// "Failed to fetch dynamically imported module" / ChunkLoadError. The fix is to
// reload ONCE so the tab pulls the fresh index.html + new chunk hashes.
//
// A short sessionStorage-guarded window prevents an infinite reload loop if the
// import is failing for a real reason (offline, a genuinely broken build): if we
// already reloaded moments ago and it STILL fails, we surface the error to the
// boundary instead of reloading again.

const RELOAD_KEY = 'kula:chunk-reload-ts';
const RELOAD_WINDOW_MS = 10_000;

function readLastReload(): number {
  try {
    return Number(window.sessionStorage.getItem(RELOAD_KEY) ?? 0);
  } catch {
    return 0; // sessionStorage blocked (private mode) — treat as never reloaded
  }
}

function markReloaded(): void {
  try {
    window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    /* best effort */
  }
}

function clearReloadMark(): void {
  try {
    window.sessionStorage.removeItem(RELOAD_KEY);
  } catch {
    /* best effort */
  }
}

export function lazyWithRetry<T extends ComponentType<any>>(
  importer: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const mod = await importer();
      clearReloadMark(); // success — let a future genuine staleness reload again
      return mod;
    } catch (err) {
      const sinceLastReload = Date.now() - readLastReload();
      if (sinceLastReload > RELOAD_WINDOW_MS) {
        markReloaded();
        window.location.reload();
        // Never resolve: React keeps showing the <Suspense> fallback until the
        // reload swaps the document, so no error flashes before we navigate away.
        return new Promise<never>(() => {});
      }
      // Already reloaded moments ago and it still fails → it's a real error.
      throw err;
    }
  });
}
